import React from 'react';
import { AlertTriangle, CheckCircle, Shield } from 'lucide-react';
import type { PaymentData, StripePaymentData, AccountInviteStatus, ProcessingJob } from '@/types/batch';

interface BatchContactInfoFormProps {
  paymentData: PaymentData;
  onInputChange: (field: keyof PaymentData, value: string | boolean) => void;
  isAuthenticated: boolean;
  accountMode: 'create' | 'login';
  onAccountModeChange: (mode: 'create' | 'login') => void;
  accountPassword: string;
  onAccountPasswordChange: (password: string) => void;
  accountPasswordConfirm: string;
  onAccountPasswordConfirmChange: (password: string) => void;
  loginPassword: string;
  onLoginPasswordChange: (password: string) => void;
  accountPasswordError: string | null;
  loginError: string | null;
  accountFeedback: string | null;
  accountInviteStatus: AccountInviteStatus | null;
  onInitializePayment: () => void;
  isInitializingIntent: boolean;
  isProcessing: boolean;
  stripeData: StripePaymentData | null;
  error: string;
  job: ProcessingJob;
}

export const BatchContactInfoForm: React.FC<BatchContactInfoFormProps> = ({
  paymentData,
  onInputChange,
  isAuthenticated,
  accountMode,
  onAccountModeChange,
  accountPassword,
  onAccountPasswordChange,
  accountPasswordConfirm,
  onAccountPasswordConfirmChange,
  loginPassword,
  onLoginPasswordChange,
  accountPasswordError,
  loginError,
  accountFeedback,
  accountInviteStatus,
  onInitializePayment,
  isInitializingIntent,
  isProcessing,
  stripeData,
  error,
  job,
}) => {
  return (
    <div className="ca-card  p-4">
      <h1 className="text-xl font-semibold text-slate-900 mb-2">Contact Information</h1>
      <p className="text-sm text-slate-600 mb-6">
        Enter the billing contact that should receive receipts and processing notifications.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Email address</label>
          <input
            type="email"
            value={paymentData.email}
            onChange={(event) => onInputChange('email', event.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
            placeholder="finance@yourcompany.com"
            required
            disabled={isAuthenticated}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Company name</label>
          <input
            type="text"
            value={paymentData.companyName}
            onChange={(event) => onInputChange('companyName', event.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
            placeholder="Your Company"
            disabled={isAuthenticated}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">First name</label>
          <input
            type="text"
            value={paymentData.firstName}
            onChange={(event) => onInputChange('firstName', event.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
            placeholder="Jane"
            required
            disabled={isAuthenticated}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Last name</label>
          <input
            type="text"
            value={paymentData.lastName}
            onChange={(event) => onInputChange('lastName', event.target.value)}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
            placeholder="Smith"
            required
            disabled={isAuthenticated}
          />
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <label className="flex items-start gap-3 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={paymentData.agreeToTerms}
            onChange={(event) => onInputChange('agreeToTerms', event.target.checked)}
            className="mt-0.5 w-4 h-4 text-slate-600 border-slate-300 rounded focus:ring-blue-500 flex-shrink-0"
            required
          />
          <span>
            I agree to the CollectABILITY terms of service and authorize a temporary hold of up to
            <span className="font-semibold"> ${job.maxPossibleCost.toFixed(2)}</span> until processing completes.
          </span>
        </label>

        <label className="flex items-start gap-3 text-sm text-slate-600">
          <input
            type="checkbox"
            checked={paymentData.fcraAcknowledgment}
            onChange={(event) => onInputChange('fcraAcknowledgment', event.target.checked)}
            className="mt-0.5 w-4 h-4 text-slate-600 border-slate-300 rounded focus:ring-blue-500 flex-shrink-0"
            required
          />
          <span>
            I acknowledge that CollectABILITY is <span className="font-semibold">not a Consumer Reporting Agency (CRA)</span> as defined by the Fair Credit Reporting Act (FCRA), and that I will not use this service for employment, credit, tenant screening, or any FCRA-related purpose.
          </span>
        </label>

        {!isAuthenticated && (
          <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600">
              <span className="font-semibold text-slate-700">
                {accountMode === 'create' ? 'Create a CollectABILITY account' : 'Sign in to your CollectABILITY account'}
              </span>
              <button
                type="button"
                onClick={() => {
                  const nextMode = accountMode === 'create' ? 'login' : 'create';
                  onAccountModeChange(nextMode);
                }}
                className="text-slate-600 hover:text-blue-800 font-medium transition-colors"
              >
                {accountMode === 'create' ? 'Already have an account? Sign in' : 'Need an account? Create one instead'}
              </button>
            </div>

            {accountMode === 'create' ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Create a password</label>
                    <input
                      type="password"
                      value={accountPassword}
                      onChange={(event) => onAccountPasswordChange(event.target.value)}
                      placeholder="At least 8 characters"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      autoComplete="new-password"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Confirm password</label>
                    <input
                      type="password"
                      value={accountPasswordConfirm}
                      onChange={(event) => onAccountPasswordConfirmChange(event.target.value)}
                      placeholder="Re-enter password"
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      autoComplete="new-password"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-600">
                  We&apos;ll create your account and sign you in automatically so this batch is saved to your dashboard.
                </p>
              </>
            ) : (
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Account password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(event) => onLoginPasswordChange(event.target.value)}
                  placeholder="Enter your password to sign in"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  autoComplete="current-password"
                />
                <p className="text-xs text-slate-600 mt-2">
                  We&apos;ll sign you in before authorizing payment so this batch links to your existing account.
                </p>
              </div>
            )}

            {accountPasswordError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-md">
                {accountPasswordError}
              </div>
            )}

            {loginError && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-md">
                {loginError}
              </div>
            )}

            {accountFeedback && (
              <div className="bg-green-50 border border-green-200 text-green-700 text-xs px-3 py-2 rounded-md">
                {accountFeedback}
              </div>
            )}
          </div>
        )}

        {accountInviteStatus?.message && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 text-xs px-3 py-2 rounded-md flex items-start gap-2">
            <CheckCircle className="w-4 h-4 mt-0.5" />
            <span>{accountInviteStatus.message}</span>
          </div>
        )}

        {accountInviteStatus?.error && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded-md flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" />
            <span>{accountInviteStatus.error}</span>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onInitializePayment}
          disabled={isInitializingIntent || isProcessing}
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isInitializingIntent ? (
            <>
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Preparing Secure Form...
            </>
          ) : (
            <>
              <Shield className="w-4 h-4" />
              Generate Secure Payment Form
            </>
          )}
        </button>
        {stripeData && !isInitializingIntent && (
          <span className="flex items-center gap-1 text-sm text-green-700">
            <CheckCircle className="w-4 h-4" />
            Payment form ready
          </span>
        )}
      </div>

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};
